import { unknownEntries, isUnknownArray, isRecord } from "../types/data";
import type { ActivityRecord, ActivityMetadata } from "../types/activity";
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { toast } from "react-toastify";
import { calculateLevel } from '../utility/levelSystem';
import CelebrationModal from '../components/CelebrationModal';
import FriendRequestModal from '../components/FriendRequestModal';
import CollectionShareModal from '../components/CollectionShareModal';
import { useFirebase } from './FirebaseContext';
import { getCachedOrFetch } from '../utility/localDataCache';
import { markNotificationsLogStale } from '../utility/activityFeedFreshness';
import { loadRecentFriendActivities } from '../utility/friendActivityFeed';
import {
  FRIEND_ROSTER_CACHE_OPTIONS,
  getFriendRosterCacheKey
} from '../utility/friendRosterCache';
import { isDemoUserId } from '../utility/demoSession';
import { useInterfacePreferences } from '../hooks/useInterfacePreferences';
import { getExperienceNotificationDocumentId } from '../utility/experienceNotification';
import type { CelebrationEvent, LevelUpContextValue, LevelUpDetails, LevelUpNotification } from './celebrationTypes';
import type { ProfileUser } from '../types/profile';

const LevelUpContext = createContext<LevelUpContextValue | undefined>(undefined);
const FRIEND_ACTIVITY_POLL_INTERVAL = 30 * 60 * 1000;
const FRIEND_ACTIVITY_QUERY_OVERLAP = 5 * 1000;
const SHOWN_CELEBRATION_KEYS_STORAGE_KEY = "movie_celebration_shown_keys_v2";
const MAX_STORED_CELEBRATION_KEYS = 300;

const sanitizeNotificationValue = (value: unknown): unknown => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (value instanceof Date) return value;
  if (isRecord(value) && typeof value.toDate === "function" && typeof value.seconds === "number") return value;
  if (isUnknownArray(value)) {
    return value
      .map(sanitizeNotificationValue)
      .filter((item) => item !== undefined);
  }
  if (typeof value === "object") {
    return unknownEntries(value).reduce<Record<string, unknown>>((result, [key, entryValue]) => {
      const nextValue = sanitizeNotificationValue(entryValue);
      if (nextValue !== undefined) result[key] = nextValue;
      return result;
    }, {});
  }
  return value;
};

const sanitizeNotificationMetadata = (metadata: unknown): Record<string, unknown> => {
  if (!metadata || typeof metadata !== "object") return {};
  const sanitized = sanitizeNotificationValue(metadata);
  return isRecord(sanitized) ? sanitized : {};
};

const getShownCelebrationKeysStorageKey = (uid: string | undefined) =>
  `${SHOWN_CELEBRATION_KEYS_STORAGE_KEY}_${uid || "anonymous"}`;

const parseShownCelebrationKeys = (rawValue: string|null) => {
  try {
    const parsed: unknown = JSON.parse(rawValue || "[]");
    return isUnknownArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [];
  }
};

const readShownCelebrationKeys = (uid: string|undefined) => {
  if (typeof window === "undefined") return new Set();
  try {
    const localValues = parseShownCelebrationKeys(
      window.localStorage.getItem(getShownCelebrationKeysStorageKey(uid))
    );
    return new Set(localValues);
  } catch {
    return new Set();
  }
};

const writeShownCelebrationKeys = (uid: string|undefined, keys: Set<unknown>) => {
  if (typeof window === "undefined") return;
  try {
    const values = [...keys].slice(-MAX_STORED_CELEBRATION_KEYS);
    window.localStorage.setItem(
      getShownCelebrationKeysStorageKey(uid),
      JSON.stringify(values)
    );
  } catch {
    // Storage can be unavailable in private contexts; in-memory dedupe still works.
  }
};

export const useLevelUp = () => {
  const context = useContext(LevelUpContext);
  if (!context) {
    throw new Error('useLevelUp must be used within a LevelUpProvider');
  }
  return context;
};

export const LevelUpProvider = ({ children }: { children?: React.ReactNode }) => {
  const { auth, firestore } = useFirebase();
  const {
    celebrationModals,
    toastNotifications,
    friendActivityNotifications
  } = useInterfacePreferences();
  const [notification, setNotification] = useState<LevelUpNotification>({
    isVisible: false,
    newLevel: 0,
    oldLevel: 0,
    experienceGained: 0,
    userPhoto: null
  });
  const [celebrationEvents, setCelebrationEvents] = useState<CelebrationEvent[]>([]);
  const bufferRef = useRef<CelebrationEvent[]>([]);
  const timerRef = useRef<number | null>(null);
  const seenFriendEventsRef = useRef(new Set<string>());
  const shownCelebrationKeysRef = useRef(
    readShownCelebrationKeys(auth.currentUser?.uid)
  );
  const friendActivityStartedAtRef = useRef(Date.now());

  const getEventKey = (event: CelebrationEvent) =>
    event.key || `${event.scope || "own"}:${event.type}:${event.title}:${event.description}`;

  const saveNotification = useCallback(async (event: CelebrationEvent) => {
    const uid = auth.currentUser?.uid;
    // Demo has its own activity log and live celebrations, but notifications
    // are a cross-session social feed. Skipping their persistence keeps demo
    // gameplay local to the temporary profile and removes an avoidable write
    // from every reward.
    if (!uid || !event || isDemoUserId(uid)) return;
    const key = getEventKey(event);
    const docId = getExperienceNotificationDocumentId(key);
    const metadata = sanitizeNotificationMetadata(event.metadata);
    const createdAt = sanitizeNotificationValue(event.createdAt || event.timestamp) || new Date();
    const experience = Number(event.experience ?? metadata.experience);
    const payload = {
      key,
      scope: event.scope || "own",
      type: event.type || "info",
      category: event.category || `${event.scope || "own"}_${event.type || "info"}`,
      title: event.title || "Событие",
      description: event.description || "",
      read: false,
      createdAt,
      ...(Number.isFinite(experience) && experience !== 0 ? { experience } : {}),
      ...(Object.keys(metadata).length ? { metadata } : {})
    };
    try {
      const notificationRef = firestore
        .collection("userNotifications")
        .doc(uid)
        .collection("notifications")
        .doc(docId);

      if (typeof firestore.runTransaction === "function") {
        await firestore.runTransaction(async (transaction) => {
          const snapshot = await transaction.get(notificationRef);
          if (snapshot.exists) return;
          transaction.set(notificationRef, payload);
        });
      } else {
        const snapshot = await notificationRef.get();
        if (snapshot.exists) return;
        await notificationRef.set(payload);
      }
      markNotificationsLogStale(uid, "owner");
    } catch (error) {
      console.warn("Не удалось сохранить уведомление:", error);
    }
  }, [auth, firestore]);

  const showToast = useCallback((event: CelebrationEvent) => {
    if (!toastNotifications) return;
    const message = event.description ? `${event.title}\n${event.description}` : event.title;
    toast(message, {
      position: "top-right",
      autoClose: 4500,
      type: event.type === "level_up" || event.type === "achievement" || event.type === "challenge" ? "success" : "info",
      className: "movie-notification-toast",
      toastId: `movie-celebration:${getEventKey(event)}`
    });
  }, [toastNotifications]);

  const shouldShowModal = (event: CelebrationEvent) => {
    if (event.mode === "toast") return false;
    if (event.scope === "friend") return false;
    if (event.type === "experience") return false;
    if (event.type === "level_up") return Number(event.level || event.metadata?.newLevel || 0) > 10;
    return ["achievement", "challenge"].includes(event.type);
  };

  const flushCelebrations = useCallback(() => {
    const events = bufferRef.current;
    bufferRef.current = [];
    timerRef.current = null;
    if (!events.length) return;

    const unique: CelebrationEvent[] = [];
    const seen = new Set();
    events.forEach((event) => {
      const key = getEventKey(event);
      if (seen.has(key)) return;
      seen.add(key);
      unique.push(event);
    });
    const freshEvents = unique.filter((event) => !shownCelebrationKeysRef.current.has(getEventKey(event)));
    freshEvents.forEach((event) => shownCelebrationKeysRef.current.add(getEventKey(event)));
    writeShownCelebrationKeys(auth.currentUser?.uid, shownCelebrationKeysRef.current);

    const modalEvents = celebrationModals
      ? freshEvents.filter(shouldShowModal)
      : [];
    const toastEvents = celebrationModals
      ? freshEvents.filter((event) => !shouldShowModal(event))
      : freshEvents;
    if (toastNotifications) {
      toast.clearWaitingQueue?.();
      toastEvents.slice(0, 3).forEach(showToast);
    }
    // Persistence is independent from the local toast history. A browser may
    // remember that it already showed a toast even though the corresponding
    // Firestore write failed or its cache was cleared. Replaying the event is
    // harmless because saveNotification uses a deterministic document id.
    unique.forEach(saveNotification);
    if (modalEvents.length) setCelebrationEvents((current) => [...current, ...modalEvents].slice(-8));
  }, [
    auth,
    celebrationModals,
    saveNotification,
    showToast,
    toastNotifications
  ]);

  const showCelebrations = useCallback((events: CelebrationEvent | CelebrationEvent[]) => {
    const normalized = (isUnknownArray(events) ? events : [events]).filter(Boolean);
    if (!normalized.length) return;
    bufferRef.current.push(...normalized);
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(flushCelebrations, 220);
  }, [flushCelebrations]);

  useEffect(() => {
    const handler = (event: Event) => {
      if (!(event instanceof CustomEvent)) return;
      const rawEvents: unknown = event.detail;
      const entries: unknown[] = isUnknownArray(rawEvents) ? rawEvents : [rawEvents];
      const events = entries.filter((entry): entry is CelebrationEvent => isRecord(entry) && typeof entry.type === "string" && typeof entry.title === "string");
      showCelebrations(events);
    };
    window.addEventListener("movie-celebration", handler);
    return () => {
      window.removeEventListener("movie-celebration", handler);
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [showCelebrations]);

  useEffect(() => {
    if (!celebrationModals) setCelebrationEvents([]);
  }, [celebrationModals]);

  useEffect(() => {
    if (!friendActivityNotifications) {
      seenFriendEventsRef.current = new Set();
      friendActivityStartedAtRef.current = Date.now();
      return undefined;
    }

    let cancelled = false;
    let pollTimer: number|null|undefined = null;
    let pollInFlight = false;

    const clearPollTimer = () => {
      if (pollTimer) {
        window.clearTimeout(pollTimer);
        pollTimer = null;
      }
    };

    const buildFriendEvent = (friend: ProfileUser, activity: { id: string; data?: ActivityRecord & { metadata?: (ActivityMetadata & { actorName?: string; weeklyCardRarity?: string }) | null } }): CelebrationEvent | null => {
      const data = activity.data || {};
      if (!data.type || !["rating", "level_up", "challenge", "weekly_card", "dungeon", "review", "achievement"].includes(data.type)) return null;
      const actor = data.metadata?.actorName || friend.name || friend.displayName || "Друг";
      const titles: Record<string, string> = {
        rating: `${actor} поставил оценку`,
        level_up: `${actor} получил уровень`,
        challenge: `${actor} завершил испытание`,
        dungeon: `${actor} проходит экспердицию`,
        weekly_card: data.metadata?.weeklyCardRarity === "legendary"
          ? `${actor} начал легендарную карту`
          : `${actor} начал карту недели`,
        review: `${actor} написал рецензию`,
        achievement: `${actor} получил достижение`
      };

      return {
        key: `${friend.uid}:${activity.id}`,
        scope: "friend",
        type: data.type,
        category: `friend_${data.type}`,
        mode: "toast",
        title: titles[data.type],
        description: data.title || data.description || data.message || "",
        metadata: {
          ...(data.metadata || {}),
          friendId: friend.uid,
          friendName: actor,
          sourceActivityId: activity.id
        },
        accent: data.type === "rating" ? "star" : data.type
      };
    };

    const loadFriendIds = async (uid: string|undefined) =>
      getCachedOrFetch(
        getFriendRosterCacheKey(uid),
        async () => {
          const snapshot = await firestore
            .collection("users")
            .doc(uid)
            .collection("friends")
            .get();

          return snapshot.docs.map((friendDoc) => ({
            uid: friendDoc.id,
            name: friendDoc.data()?.name,
            displayName: friendDoc.data()?.displayName
          }));
        },
        FRIEND_ROSTER_CACHE_OPTIONS
      );

    const pollFriendActivities = async (uid: string) => {
      if (cancelled || !uid || pollInFlight) return false;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return false;

      pollInFlight = true;
      const pollStartedAt = Date.now();
      try {
        const friends = await loadFriendIds(uid);
        if (cancelled) return false;
        const friendsById = new Map<string, ProfileUser>(friends.map((friend) => [String(friend.uid), friend]));
        const activities = await loadRecentFriendActivities({
          firestore,
          friendIds: [...friendsById.keys()],
          since: Math.max(
            0,
            Number(friendActivityStartedAtRef.current) - FRIEND_ACTIVITY_QUERY_OVERLAP
          )
        });
        if (cancelled) return false;
        const events: CelebrationEvent[] = [];

        activities.forEach((activity) => {
          const friend = friendsById.get(String(activity.actorId));
          if (!friend) return;
          const key = `${friend.uid}:${activity.id}`;
          if (seenFriendEventsRef.current.has(key)) return;
          seenFriendEventsRef.current.add(key);
          const event = buildFriendEvent(friend, activity);
          if (event) events.push(event);
        });

        if (events.length) showCelebrations(events);
        // Use the request start rather than its finish so an event written
        // while the query is in flight is picked up by the next poll.
        friendActivityStartedAtRef.current = pollStartedAt;
        return true;
      } catch (error) {
        console.warn("Не удалось проверить события друзей:", error);
        return false;
      } finally {
        pollInFlight = false;
      }
    };

    const schedulePolling = (uid: string) => {
      clearPollTimer();
      pollTimer = window.setTimeout(async () => {
        await pollFriendActivities(uid);
        if (!cancelled) schedulePolling(uid);
      }, FRIEND_ACTIVITY_POLL_INTERVAL);
    };

    const unsubscribeAuthState = auth.onAuthStateChanged((user) => {
      clearPollTimer();
      seenFriendEventsRef.current = new Set();
      shownCelebrationKeysRef.current = readShownCelebrationKeys(user?.uid);
      bufferRef.current = [];
      friendActivityStartedAtRef.current = Date.now();
      if (!user?.uid || isDemoUserId(user.uid)) return;
      // Do not spend reads merely because Layout mounted. The first bounded
      // check is deferred until the tab has stayed open for the poll interval.
      schedulePolling(user.uid);
    });

    const handleVisibilityChange = () => {
      const uid = auth.currentUser?.uid;
      const pollAge = Date.now() - Number(friendActivityStartedAtRef.current || 0);
      if (
        document.visibilityState === "visible" &&
        uid &&
        !isDemoUserId(uid) &&
        pollAge >= FRIEND_ACTIVITY_POLL_INTERVAL
      ) {
        void pollFriendActivities(uid).finally(() => {
          if (!cancelled) schedulePolling(uid);
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelled = true;
      clearPollTimer();
      unsubscribeAuthState?.();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [auth, friendActivityNotifications, firestore, showCelebrations]);

  const checkAndShowLevelUp = useCallback((oldExperience: number | LevelUpDetails, newExperience?: number, experienceGained?: number, userPhoto: string | null = null) => {
    if (typeof oldExperience === "object" && oldExperience !== null) {
      const data = oldExperience;
      setNotification({
        isVisible: true,
        newLevel: data.newLevel,
        oldLevel: data.oldLevel || Math.max(0, Number(data.newLevel || 1) - 1),
        experienceGained: data.experienceGained || 0,
        userPhoto: data.userPhoto || null
      });
      showCelebrations({
        scope: "own",
        type: "level_up",
        category: "progress_level",
        mode: Number(data.newLevel || 0) > 10 ? "modal" : "toast",
        level: data.newLevel,
        title: `Новый уровень: ${data.newLevel}`,
        description: `Достигнут уровень ${data.newLevel}`,
        key: `level:${data.newLevel}`
      });
      return true;
    }

    console.log('🎯 checkAndShowLevelUp called with:', {
      oldExperience,
      newExperience,
      experienceGained,
      userPhoto: userPhoto ? 'provided' : 'null'
    });

    // Используем тот же расчет, что и в профиле
    const totalExp = newExperience;
    const newLevel = calculateLevel(Number(totalExp));

    console.log('📊 Level calculation in checkAndShowLevelUp:', {
      totalExp,
      calculatedLevel: newLevel,
      oldExperience,
      newExperience,
      experienceGained
    });

    // Рассчитываем предыдущий уровень на основе общего опыта
    const oldLevel = calculateLevel(oldExperience);

    if (newLevel > oldLevel) {
      console.log('🎉 Setting notification with:', {
        newLevel,
        oldLevel,
        experienceGained,
        totalExp
      });

      setNotification({
        isVisible: true,
        newLevel,
        oldLevel,
        experienceGained,
        userPhoto
      });
      showCelebrations({
        scope: "own",
        type: "level_up",
        category: "progress_level",
        mode: Number(newLevel || 0) > 10 ? "modal" : "toast",
        level: newLevel,
        title: `Новый уровень: ${newLevel}`,
        description: `Достигнут уровень ${newLevel}`,
        key: `level:${newLevel}`
      });
      return true;
    }
    return false;
  }, [showCelebrations]);

  const hideNotification = useCallback(() => {
    setNotification(prev => ({
      ...prev,
      isVisible: false
    }));
  }, []);

  const value = {
    checkAndShowLevelUp,
    hideNotification,
    notification
  };

  return (
    <LevelUpContext.Provider value={value}>
      {children}
      {celebrationModals && (
        <CelebrationModal
          events={celebrationEvents}
          onClose={() => setCelebrationEvents([])}
        />
      )}
      <FriendRequestModal />
      <CollectionShareModal />
    </LevelUpContext.Provider>
  );
};
